import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, qs } from "@/lib/api";
import { useKeys } from "@/lib/queries";

export interface SlackConnection {
  id: number;
  tenant_id: number;
  name: string;
  team_id: string;
  team_name: string;
  team_url: string;
  enabled: boolean;
  status: string;
  last_error: string | null;
}
export interface SlackChannel {
  id: string;
  name: string;
  is_private: boolean;
}
export function useSlackConnections(enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["slack", "connections"]),
    queryFn: () => api.get<SlackConnection[]>("/integrations/slack/connections"),
    enabled,
  });
}
export function useSlackChannels(connectionId: number | null, enabled = true) {
  const keys = useKeys();
  return useQuery({
    queryKey: keys.scoped(["slack", "channels", connectionId]),
    enabled: enabled && connectionId !== null,
    queryFn: async () => {
      const channels: SlackChannel[] = [];
      let cursor = "";
      const seen = new Set<string>();
      do {
        const page = await api.get<{
          channels: SlackChannel[];
          next_cursor: string;
        }>(`/integrations/slack/connections/${connectionId}/channels${qs({ cursor })}`);
        channels.push(...page.channels);
        cursor = page.next_cursor;
        if (cursor && seen.has(cursor)) throw new Error("Slack repeated a channel page; try again.");
        seen.add(cursor);
      } while (cursor);
      return channels.sort((a, b) => a.name.localeCompare(b.name));
    },
  });
}
export function useSaveSlackConnection() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id?: number;
      name: string;
      token?: string;
      tenant_id?: number;
      enabled?: boolean;
    }) =>
      id
        ? api.put<SlackConnection>(`/integrations/slack/connections/${id}`, body)
        : api.post<SlackConnection>("/integrations/slack/connections", body),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.scoped(["slack"]) }),
  });
}
export function useDeleteSlackConnection() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete(`/integrations/slack/connections/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.scoped(["slack"]) }),
  });
}
export function useCallAlerts() {
  const keys = useKeys();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.post(`/webhooks/endpoints/${id}/call-alerts`),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.scoped(["webhooks"]) }),
  });
}
